/**
 * PdfMarkupViewer - Autodesk Build-style PDF viewer with collaborative markups
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { ActiveViewers } from '../../common/ViewingTemplate';
import { ListenButton } from '../../common/AudioGuide';
import HelpWithVideoTour from '../../Help/HelpWithVideoTour';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// .js extension (not .mjs): pm2's static server sends .mjs as text/plain,
// which browsers refuse to execute as a module worker -> "Failed to load PDF file."
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const TOOLS = { hand: 'hand', select: 'select', pen: 'pen', text: 'text', rectangle: 'rectangle', ellipse: 'ellipse', arrow: 'arrow' };
const COLORS_SOLID = ['#e53935', '#d81b60', '#8e24aa', '#5e35b1', '#3949ab', '#1e88e5', '#039be5', '#00acc1', '#00897b', '#43a047', '#7cb342', '#c0ca33', '#fdd835', '#ffb300', '#fb8c00', '#f4511e', '#6d4c41', '#000000'];
const BORDER_OPTIONS = [{ value: 'transparent', label: 'No colour' }, ...COLORS_SOLID.map((c) => ({ value: c, label: c }))];
const COLORS = COLORS_SOLID;
const FILL_OPTIONS = [
  { value: '', label: 'No fill' },
  { value: 'rgba(227, 242, 253, 0.4)', label: 'Light blue' },
  { value: 'rgba(255, 235, 59, 0.35)', label: 'Yellow' },
  { value: 'rgba(255, 205, 210, 0.4)', label: 'Light red' },
  { value: 'rgba(200, 230, 201, 0.4)', label: 'Light green' },
  ...COLORS.map((c) => ({ value: c, label: c })),
];
const hasFill = (v) => v && v !== '' && v !== 'transparent';
const STROKE_WIDTHS = [1, 2, 3, 4, 6, 8];
const ZOOM_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.5, 3];
const OPACITY_OPTIONS = [100, 80, 60, 40, 20, 10];

const ToolIcons = {
  select: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4l7 16 2-7 7-2L4 4z" /></svg>),
  pen: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19l7-7 3 3-7 7-3-3z" /><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" /></svg>),
  rectangle: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="1" /></svg>),
  ellipse: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /></svg>),
  arrow: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7" /></svg>),
  text: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 7V4h16v3M9 20h6M12 4v16" /></svg>),
  hand: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 11V6a2 2 0 00-2-2v0a2 2 0 00-2 2v0M14 10V4a2 2 0 00-2-2v0a2 2 0 00-2 2v2M10 10.5V6a2 2 0 00-2-2v0a2 2 0 00-2 2v8" /><path d="M18 8a2 2 0 114 0v6a8 8 0 01-16 0v-2a2 2 0 014 0v4" /></svg>),
  ruler: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 3H6l-4 18 18 4 4-18z" /><path d="M10 14l4-4" /><path d="M14 10l4-4" /></svg>),
  delete: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></svg>),
  undo: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 10h10a5 5 0 015 5v2M3 10l4-4M3 10l4 4" /></svg>),
  redo: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10H11a5 5 0 00-5 5v2M21 10l-4-4M21 10l-4 4" /></svg>),
  settings: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" /></svg>),
  fullscreen: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" /></svg>),
  cloud: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z" /></svg>),
  camera: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" /><circle cx="12" cy="13" r="4" /></svg>),
  check: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><path d="M22 4L12 14.01l-3-3" /></svg>),
  fitView: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="2" /><path d="M9 2v20M2 12h20" /></svg>),
  rotate: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 4v6h6M23 20v-6h-6" /><path d="M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15" /></svg>),
  compare: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="18" rx="2" /><line x1="12" y1="3" x2="12" y2="21" /><path d="M6 12h2M16 12h2" /></svg>),
  help: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>),
  opacity: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2.69l5.66 5.66a8 8 0 11-11.31 0z" /></svg>),
  edit: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>),
  duplicate: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>),
  reference: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" /></svg>),
};

const HelpModal = ({ onClose, portalBorder }) => {
  const b = portalBorder;
  const Section = ({ title, children }) => (<><h4 style={{ margin: '16px 0 8px 0', fontSize: '15px', color: '#111827', fontWeight: 600 }}>{title}</h4>{children}</>);
  const row = (label, desc) => (<tr key={label} style={{ borderBottom: `1px solid ${b}` }}><td style={{ padding: '6px 8px', fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top', fontSize: '12px' }}>{label}</td><td style={{ padding: '6px 8px', color: '#4b5563', fontSize: '12px' }}>{desc}</td></tr>);
  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 10002, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
      <div style={{ background: '#fff', color: '#374151', borderRadius: '12px', maxWidth: 640, width: '100%', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', border: `1px solid ${b}` }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${b}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Sheets Viewer — Help Guide</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ListenButton compact guideId="sheets-viewer-help" title="Sheets Viewer Help" />
            <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#6b7280' }}>×</button>
          </div>
        </div>
        <div style={{ overflow: 'auto', padding: '20px', flex: 1, fontSize: '13px', lineHeight: 1.7 }}>
<HelpWithVideoTour guideId="sheets-viewer-help" title="Sheets Viewer Help" accent="#0369a1">

          <Section title="Overview">
            <p style={{ margin: '0 0 12px 0' }}>This is the Technical Sheets PDF Viewer — an Autodesk Build-style tool for viewing, annotating, and collaborating on technical PDF sheets. All markups are saved automatically and visible to all project members in real time.</p>
          </Section>

          <Section title="Drawing Tools">
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
              <tbody>
                {row('Hand (Pan)', 'Click and drag to scroll the PDF around. You must zoom in first (Ctrl + scroll wheel, or use the zoom buttons) for pan to have effect — at 100% the sheet fits in the view.')}
                {row('Select', 'Click any markup to select it. Shows a context menu: Edit, Duplicate, Reference, Delete. Selected markups can be dragged to move and resized via corner handles.')}
                {row('Pen', 'Freehand drawing. Click and drag to draw free lines.')}
                {row('Rectangle', 'Click and drag to draw a rectangle. Fill and border colours apply.')}
                {row('Ellipse', 'Click and drag to draw an ellipse or circle. Fill and border colours apply.')}
                {row('Arrow', 'Click and drag to draw an arrow from start point to end point.')}
                {row('Text', 'Click anywhere on the PDF to place a text annotation. A popup appears to type your text.')}
              </tbody>
            </table>
          </Section>

          <Section title="Colours, Fill & Opacity">
            <ul style={{ margin: '0 0 12px 0', paddingLeft: 20 }}>
              <li><strong>Border colour</strong> — Sets the stroke/outline colour. Includes "No colour" for transparent borders. If a markup is selected, changing this updates that markup instantly.</li>
              <li><strong>Fill colour</strong> — Sets the fill for rectangles and ellipses. "No fill" means transparent. Semi-transparent presets (light blue, yellow, red, green) let the PDF text show through beneath the shape.</li>
              <li><strong>Opacity</strong> — Controls overall transparency of the markup (100% = fully visible, 10% = almost invisible). Works for new drawings and can be changed on selected markups via the dropdown.</li>
              <li><strong>Stroke width</strong> — Controls the thickness of lines and shape borders (1px to 8px).</li>
            </ul>
          </Section>

          <Section title="Selecting & Modifying Markups">
            <ul style={{ margin: '0 0 12px 0', paddingLeft: 20 }}>
              <li><strong>Click a markup</strong> with the Select tool to select it. A context menu appears with 4 actions.</li>
              <li><strong>Edit</strong> — Closes the menu, keeps the markup selected so you can change its properties in the toolbar.</li>
              <li><strong>Duplicate</strong> — Creates an identical copy of the markup, slightly offset.</li>
              <li><strong>Reference</strong> — Link the markup to an issue or document (coming soon).</li>
              <li><strong>Delete</strong> — Permanently removes the markup.</li>
              <li><strong>Move</strong> — With a markup already selected, click and drag it to reposition it on the page.</li>
              <li><strong>Resize</strong> — Blue square handles appear on the corners of rectangles/ellipses, or on the endpoints of arrows. Drag any handle to resize.</li>
              <li><strong>Inline property editing</strong> — When a markup is selected, extra dropdowns appear at the bottom of the floating toolbar: border colour, fill (for shapes), stroke width, and opacity. Changes are saved immediately.</li>
              <li><strong>Any user</strong> with project access can modify or delete any markup, not just their own.</li>
            </ul>
          </Section>

          <Section title="Zoom & Navigation">
            <ul style={{ margin: '0 0 12px 0', paddingLeft: 20 }}>
              <li><strong>Ctrl + mouse wheel</strong> — Zoom in/out on the PDF only (does not zoom the browser).</li>
              <li><strong>Zoom dropdown</strong> — Select a specific zoom level from 50% to 300%.</li>
              <li><strong>+/− buttons</strong> — Adjust zoom by 25% per click.</li>
              <li><strong>Fit to View</strong> — Resets zoom to 100% so the sheet fits in the window.</li>
              <li><strong>Rotate</strong> — Rotates the entire PDF by 90° each click (0° → 90° → 180° → 270°).</li>
              <li><strong>Multi-page</strong> — If the PDF has multiple pages, use the ← → arrows or page indicator in the toolbar.</li>
            </ul>
          </Section>

          <Section title="Undo / Redo">
            <ul style={{ margin: '0 0 12px 0', paddingLeft: 20 }}>
              <li><strong>Ctrl+Z</strong> — Undo the last action (drawing, modifying, deleting, zooming, rotating).</li>
              <li><strong>Ctrl+Y</strong> or <strong>Ctrl+Shift+Z</strong> — Redo the last undone action.</li>
              <li>Toolbar buttons also work. Up to 50 actions are remembered per session.</li>
            </ul>
          </Section>

          <Section title="Other Features">
            <ul style={{ margin: '0 0 12px 0', paddingLeft: 20 }}>
              <li><strong>Audit log</strong> — View a timestamped log of who created, modified, or deleted markups on this sheet.</li>
              <li><strong>Settings</strong> — Configuration (performance optimization, display units), Navigation (zoom speed, mouse settings), Appearance (sheet colour, loading animation).</li>
              <li><strong>Compare revisions</strong> — Compare two revisions of the same sheet to see what changed between versions. Changes will be highlighted in red (coming soon).</li>
              <li><strong>Viewing / Presence</strong> — See who else is currently viewing this sheet in the top-right corner.</li>
              <li><strong>Auto-save</strong> — All markups are saved automatically within seconds. No manual save needed.</li>
              <li><strong>Collaborative</strong> — Other users' markups appear within seconds via polling. All annotations are shared.</li>
            </ul>
          </Section>

          <Section title="Keyboard Shortcuts">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {row('Ctrl + Z', 'Undo')}
                {row('Ctrl + Y', 'Redo')}
                {row('Ctrl + Shift + Z', 'Redo (alternative)')}
                {row('Ctrl + Mouse Wheel', 'Zoom in/out (PDF only)')}
                {row('Escape', 'Cancel text input')}
                {row('Enter', 'Submit text annotation')}
              </tbody>
            </table>
          </Section>

</HelpWithVideoTour>
        </div>
      </div>
    </div>
  );
};

const PdfMarkupViewer = ({
  pdfUrl,
  sheetId,
  sheetTitle,
  onClose,
  projectId,
  viewers = [],
  isConnected = false,
  connectionState = 'disconnected',
}) => {
  const { instance, accounts } = useMsal();
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState({ width: 800, height: 0 });
  const [markups, setMarkups] = useState([]);
  const [, setLoadingMarkups] = useState(true);
  const [saveStatus, setSaveStatus] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [tool, setTool] = useState(TOOLS.select);
  const [selectedMarkupId, setSelectedMarkupId] = useState(null);
  const [panning, setPanning] = useState(false);
  const panStartRef = useRef(null);
  const [movingMarkup, setMovingMarkup] = useState(null);
  const moveStartRef = useRef(null);
  const [resizingMarkup, setResizingMarkup] = useState(null);
  const resizeStartRef = useRef(null);
  const [color, setColor] = useState('#1e88e5');
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [drawing, setDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState([]);
  const [currentShape, setCurrentShape] = useState(null);
  const [textInput, setTextInput] = useState(null);
  const [textValue, setTextValue] = useState('');
  const [pendingSave, setPendingSave] = useState(null);
  const saveTimeoutRef = useRef(null);
  const pollRef = useRef(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState(null);
  const [pageReady, setPageReady] = useState(false);
  const [displayWidth, setDisplayWidth] = useState(800);
  const [zoom, setZoom] = useState(1);
  const [toolbarVertical, setToolbarVertical] = useState(true);
  const [toolbarDragged, setToolbarDragged] = useState(false);
  const [toolbarPosition, setToolbarPosition] = useState({ x: 0, y: 12 });
  const [fillColor, setFillColor] = useState('');
  const [opacity, setOpacity] = useState(100);
  const [showFillPicker, setShowFillPicker] = useState(false);
  const [showBorderPicker, setShowBorderPicker] = useState(false);
  const toolbarRef = useRef(null);
  const toolbarContainerRef = useRef(null);
  const dragStartRef = useRef(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState('config');
  const [compareOpen, setCompareOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  useEffect(() => {
    if (!containerRef.current) return;
    const w = Math.round(containerRef.current.clientWidth || 800) - 48;
    setDisplayWidth(Math.min(Math.max(w, 400), 1000));
  }, []);
  const effectiveWidth = displayWidth;

  const handleToolbarDragStart = useCallback((e) => {
    e.preventDefault();
    if (!toolbarRef.current || !toolbarContainerRef.current) return;
    const rect = toolbarRef.current.getBoundingClientRect();
    const cont = toolbarContainerRef.current.getBoundingClientRect();
    dragStartRef.current = { clientX: e.clientX, clientY: e.clientY, left: rect.left - cont.left, top: rect.top - cont.top };
    const onMove = (ev) => {
      if (!dragStartRef.current) return;
      setToolbarPosition({ x: Math.max(0, dragStartRef.current.left + ev.clientX - dragStartRef.current.clientX), y: Math.max(0, dragStartRef.current.top + ev.clientY - dragStartRef.current.clientY) });
      setToolbarDragged(true);
    };
    const onUp = () => { dragStartRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  const fetchMarkups = useCallback(async () => {
    if (!sheetId) return;
    try {
      const res = await authenticatedApiCall(instance, accounts, 'GET', `/technical-sheets/sheets/${sheetId}/markups`);
      if (res.data?.success && Array.isArray(res.data.markups)) setMarkups(res.data.markups);
    } catch (e) { console.error('Fetch markups error:', e); } finally { setLoadingMarkups(false); }
  }, [sheetId, instance, accounts]);

  useEffect(() => { fetchMarkups(); }, [fetchMarkups]);
  useEffect(() => {
    if (!sheetId) return;
    pollRef.current = setInterval(fetchMarkups, 4000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [sheetId, fetchMarkups]);

  const saveMarkup = useCallback(async (page_number, type, data) => {
    if (!sheetId) return;
    setSaveStatus('saving'); setSaveError(null);
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `/technical-sheets/sheets/${sheetId}/markups`, { page_number, type, data });
      setSaveStatus('saved'); setSaveError(null); fetchMarkups();
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (e) {
      const msg = e.response?.data?.error || e.response?.data?.message || e.message || 'Save failed';
      setSaveError(msg); setSaveStatus('error');
      setTimeout(() => { setSaveStatus(null); setSaveError(null); }, 5000);
    }
  }, [sheetId, instance, accounts, fetchMarkups]);

  const flushPendingSave = useCallback(() => {
    if (saveTimeoutRef.current) { clearTimeout(saveTimeoutRef.current); saveTimeoutRef.current = null; }
    if (pendingSave) { const { page_number, type, data } = pendingSave; setPendingSave(null); saveMarkup(page_number, type, data); }
  }, [pendingSave, saveMarkup]);
  useEffect(() => { return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); flushPendingSave(); }; }, [flushPendingSave]);

  const fetchAudit = useCallback(async () => {
    if (!sheetId) { setAuditError('No sheet selected'); setAuditLoading(false); return; }
    setAuditLoading(true); setAuditError(null);
    const tryFetch = async (url) => {
      const res = await authenticatedApiCall(instance, accounts, 'GET', url);
      if (res.data?.success && Array.isArray(res.data.audit)) return res.data.audit;
      return null;
    };
    try {
      let audit = await tryFetch(`/technical-sheets/audit?sheetId=${sheetId}`);
      if (audit == null) audit = await tryFetch(`/technical-sheets/sheets/${sheetId}/audit`);
      if (audit != null) { setAuditLog(audit); setAuditError(null); }
      else { setAuditLog([]); setAuditError('Could not load audit log'); }
    } catch (e) {
      const msg = e.response?.data?.error || e.response?.data?.message || e.message || 'Failed to load audit log';
      setAuditError(e.response?.status ? `${msg} (${e.response.status})` : msg); setAuditLog([]);
    } finally { setAuditLoading(false); }
  }, [sheetId, instance, accounts]);
  const openAudit = useCallback(() => { setAuditError(null); setAuditOpen(true); fetchAudit(); }, [fetchAudit]);

  const pushUndo = useCallback((action) => {
    setUndoStack((prev) => [...prev.slice(-49), action]);
    setRedoStack([]);
  }, []);

  const deleteMarkup = useCallback(async (markupId) => {
    if (!markupId || String(markupId).startsWith('opt-')) return;
    const existing = markups.find((m) => m.id === markupId || String(m.id) === String(markupId));
    if (existing) pushUndo({ type: 'delete', markup: JSON.parse(JSON.stringify(existing)) });
    setSaveStatus('saving'); setSaveError(null);
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `/technical-sheets/markups/${markupId}`);
      setMarkups((prev) => prev.filter((m) => m.id !== markupId && String(m.id) !== String(markupId)));
      setSelectedMarkupId(null); setContextMenu(null);
      setSaveStatus('saved'); setTimeout(() => setSaveStatus(null), 2000);
      fetchMarkups(); fetchAudit();
    } catch (e) {
      const msg = e.response?.data?.error || e.response?.data?.message || e.message || 'Delete failed';
      setSaveError(msg); setSaveStatus('error');
      setTimeout(() => { setSaveStatus(null); setSaveError(null); }, 5000);
    }
  }, [instance, accounts, fetchMarkups, fetchAudit, markups, pushUndo]);

  const updateMarkup = useCallback(async (markupId, data) => {
    if (!markupId || String(markupId).startsWith('opt-')) return;
    const existing = markups.find((m) => m.id === markupId || String(m.id) === String(markupId));
    if (existing) pushUndo({ type: 'update', markupId, oldData: JSON.parse(JSON.stringify(existing.data || {})) });
    setSaveStatus('saving'); setSaveError(null);
    setMarkups((prev) => prev.map((m) => (m.id === markupId || String(m.id) === String(markupId) ? { ...m, data } : m)));
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', `/technical-sheets/markups/${markupId}`, { data });
      setSaveStatus('saved'); setTimeout(() => setSaveStatus(null), 2000);
      fetchMarkups(); fetchAudit();
    } catch (e) {
      const msg = e.response?.data?.error || e.response?.data?.message || e.message || 'Update failed';
      setSaveError(msg); setSaveStatus('error');
      setTimeout(() => { setSaveStatus(null); setSaveError(null); }, 5000);
      fetchMarkups();
    }
  }, [instance, accounts, fetchMarkups, fetchAudit, markups, pushUndo]);

  const handleUndo = useCallback(() => {
    if (undoStack.length === 0) return;
    const action = undoStack[undoStack.length - 1];
    setUndoStack((prev) => prev.slice(0, -1));
    if (action.type === 'zoom') {
      setRedoStack((prev) => [...prev, { type: 'zoom', oldValue: zoom }]);
      setZoom(action.oldValue);
    } else if (action.type === 'rotation') {
      setRedoStack((prev) => [...prev, { type: 'rotation', oldValue: rotation }]);
      setRotation(action.oldValue);
    } else if (action.type === 'delete' && action.markup) {
      const m = action.markup;
      setSaveStatus('saving');
      authenticatedApiCall(instance, accounts, 'POST', `/technical-sheets/sheets/${sheetId}/markups`, { page_number: m.page_number, type: m.type, data: m.data })
        .then(() => { setSaveStatus('saved'); setTimeout(() => setSaveStatus(null), 2000); fetchMarkups(); fetchAudit(); })
        .catch(() => { setSaveStatus('error'); setTimeout(() => setSaveStatus(null), 3000); });
      setRedoStack((prev) => [...prev, action]);
    } else if (action.type === 'update' && action.markupId && action.oldData) {
      const curr = markups.find((m) => m.id === action.markupId || String(m.id) === String(action.markupId));
      setRedoStack((prev) => [...prev, { type: 'update', markupId: action.markupId, oldData: curr ? JSON.parse(JSON.stringify(curr.data || {})) : null }]);
      setMarkups((prev) => prev.map((m) => (m.id === action.markupId || String(m.id) === String(action.markupId) ? { ...m, data: action.oldData } : m)));
      authenticatedApiCall(instance, accounts, 'PUT', `/technical-sheets/markups/${action.markupId}`, { data: action.oldData })
        .then(() => { setSaveStatus('saved'); setTimeout(() => setSaveStatus(null), 2000); fetchMarkups(); })
        .catch(() => { setSaveStatus('error'); setTimeout(() => setSaveStatus(null), 3000); });
    } else if (action.type === 'create' && action.markupId) {
      setRedoStack((prev) => [...prev, action]);
      authenticatedApiCall(instance, accounts, 'DELETE', `/technical-sheets/markups/${action.markupId}`)
        .then(() => { fetchMarkups(); })
        .catch(() => {});
    }
  }, [undoStack, instance, accounts, sheetId, fetchMarkups, fetchAudit, markups, zoom, rotation]);

  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) return;
    const action = redoStack[redoStack.length - 1];
    setRedoStack((prev) => prev.slice(0, -1));
    if (action.type === 'zoom') {
      setUndoStack((prev) => [...prev, { type: 'zoom', oldValue: zoom }]);
      setZoom(action.oldValue);
    } else if (action.type === 'rotation') {
      setUndoStack((prev) => [...prev, { type: 'rotation', oldValue: rotation }]);
      setRotation(action.oldValue);
    } else if (action.type === 'delete' && action.markup) {
      setUndoStack((prev) => [...prev, action]);
      authenticatedApiCall(instance, accounts, 'DELETE', `/technical-sheets/markups/${action.markup.id}`)
        .then(() => { fetchMarkups(); fetchAudit(); })
        .catch(() => {});
    } else if (action.type === 'update' && action.markupId && action.oldData) {
      const curr = markups.find((m) => m.id === action.markupId || String(m.id) === String(action.markupId));
      setUndoStack((prev) => [...prev, { type: 'update', markupId: action.markupId, oldData: curr ? JSON.parse(JSON.stringify(curr.data || {})) : null }]);
      setMarkups((prev) => prev.map((m) => (m.id === action.markupId || String(m.id) === String(action.markupId) ? { ...m, data: action.oldData } : m)));
      authenticatedApiCall(instance, accounts, 'PUT', `/technical-sheets/markups/${action.markupId}`, { data: action.oldData })
        .then(() => { fetchMarkups(); })
        .catch(() => {});
    }
  }, [redoStack, instance, accounts, fetchMarkups, fetchAudit, markups, zoom, rotation]);

  const duplicateMarkup = useCallback(async (markupId) => {
    const m = markups.find((x) => x.id === markupId || String(x.id) === String(markupId));
    if (!m || !sheetId) return;
    const data = JSON.parse(JSON.stringify(m.data || {}));
    const offset = 0.03;
    if (data.x != null) data.x += offset;
    if (data.y != null) data.y += offset;
    if (data.x1 != null) { data.x1 += offset; data.y1 += offset; data.x2 += offset; data.y2 += offset; }
    if (Array.isArray(data.points)) data.points = data.points.map(([x, y]) => [x + offset, y + offset]);
    setSaveStatus('saving');
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `/technical-sheets/sheets/${sheetId}/markups`, { page_number: m.page_number, type: m.type, data });
      setSaveStatus('saved'); setTimeout(() => setSaveStatus(null), 2000);
      fetchMarkups(); fetchAudit();
    } catch (e) {
      setSaveError(e.message); setSaveStatus('error');
      setTimeout(() => { setSaveStatus(null); setSaveError(null); }, 5000);
    }
    setContextMenu(null);
  }, [markups, sheetId, instance, accounts, fetchMarkups, fetchAudit]);

  const scheduleSave = useCallback((page_number, type, data) => {
    setPendingSave({ page_number, type, data });
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null;
      setPendingSave((prev) => { if (prev) saveMarkup(prev.page_number, prev.type, prev.data); return null; });
    }, 1200);
  }, [saveMarkup]);

  const onDocumentLoadSuccess = ({ numPages: n }) => setNumPages(n);
  const onPageLoadSuccess = (page) => {
    const w = effectiveWidth * zoom;
    const h = page.height ? (page.height / page.width) * w : w;
    setPageSize({ width: w, height: h });
    setPageReady(true);
  };
  useEffect(() => { setPageReady(false); }, [currentPage]);
  useEffect(() => {
    setPageSize((prev) => {
      if (prev.width <= 0 || prev.height <= 0) return prev;
      const newW = effectiveWidth * zoom;
      return { width: newW, height: prev.height * (newW / prev.width) };
    });
  }, [zoom, effectiveWidth]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault(); e.stopPropagation();
      setZoom((prev) => {
        const next = Math.min(3, Math.max(0.5, prev + (e.deltaY > 0 ? -0.25 : 0.25)));
        if (next !== prev) pushUndo({ type: 'zoom', oldValue: prev });
        return next;
      });
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [pdfUrl, pushUndo]);

  const fitToView = useCallback(() => {
    setZoom((prev) => { if (prev !== 1) pushUndo({ type: 'zoom', oldValue: prev }); return 1; });
  }, [pushUndo]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); handleUndo(); }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) { e.preventDefault(); handleRedo(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleUndo, handleRedo]);

  const getCanvasPoint = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return { x: (e.clientX - rect.left) * (canvas.width / rect.width), y: (e.clientY - rect.top) * (canvas.height / rect.height) };
  };

  const hitTestMarkup = useCallback((point) => {
    const w = pageSize.width, h = pageSize.height;
    if (!w || !h) return null;
    const pad = 8;
    const pageMarkups = markups.filter((m) => m.page_number === currentPage && !String(m.id).startsWith('opt-'));
    for (let i = pageMarkups.length - 1; i >= 0; i--) {
      const m = pageMarkups[i]; const d = m.data || {}; let hit = false;
      if (m.type === 'pen' && Array.isArray(d.points) && d.points.length) {
        const px = d.points.map(([a, b]) => [a * w, b * h]);
        let minX = px[0][0], maxX = px[0][0], minY = px[0][1], maxY = px[0][1];
        px.forEach(([x, y]) => { minX = Math.min(minX, x); maxX = Math.max(maxX, x); minY = Math.min(minY, y); maxY = Math.max(maxY, y); });
        hit = point.x >= minX - pad && point.x <= maxX + pad && point.y >= minY - pad && point.y <= maxY + pad;
      } else if (m.type === 'rectangle') {
        const x = (d.x ?? 0) * w, y = (d.y ?? 0) * h, width = (d.width ?? 0) * w, height = (d.height ?? 0) * h;
        hit = point.x >= Math.min(x, x + width) - pad && point.x <= Math.max(x, x + width) + pad && point.y >= Math.min(y, y + height) - pad && point.y <= Math.max(y, y + height) + pad;
      } else if (m.type === 'ellipse') {
        const x = (d.x ?? 0) * w, y = (d.y ?? 0) * h, rw = Math.abs((d.width ?? 0) * w / 2), rh = Math.abs((d.height ?? 0) * h / 2);
        const cx = x + rw, cy = y + rh;
        hit = (point.x - cx) ** 2 / (rw * rw || 1) + (point.y - cy) ** 2 / (rh * rh || 1) <= 1.2;
      } else if (m.type === 'arrow') {
        const x1 = (d.x1 ?? 0) * w, y1 = (d.y1 ?? 0) * h, x2 = (d.x2 ?? 0) * w, y2 = (d.y2 ?? 0) * h;
        hit = point.x >= Math.min(x1, x2) - pad && point.x <= Math.max(x1, x2) + pad && point.y >= Math.min(y1, y2) - pad && point.y <= Math.max(y1, y2) + pad;
      } else if (m.type === 'text' && d.text) {
        const x = (d.x ?? 0) * w, y = (d.y ?? 0) * h;
        hit = point.x >= x - pad && point.x <= x + 150 && point.y >= y - 16 && point.y <= y + pad;
      }
      if (hit) return m;
    }
    return null;
  }, [markups, currentPage, pageSize]);

  const HANDLE_SIZE = 10;
  const getSelectedBbox = useCallback(() => {
    if (!selectedMarkupId || !pageSize.width || !pageSize.height) return null;
    const m = markups.find((x) => x.id === selectedMarkupId || String(x.id) === String(selectedMarkupId));
    if (!m || !m.data) return null;
    const d = m.data, w = pageSize.width, h = pageSize.height;
    if (m.type === 'rectangle' || m.type === 'ellipse') {
      const x = (d.x ?? 0) * w, y = (d.y ?? 0) * h, width = (d.width ?? 0) * w, height = (d.height ?? 0) * h;
      return { x1: Math.min(x, x + width), y1: Math.min(y, y + height), x2: Math.max(x, x + width), y2: Math.max(y, y + height), markup: m };
    }
    if (m.type === 'arrow') {
      const x1 = (d.x1 ?? 0) * w, y1 = (d.y1 ?? 0) * h, x2 = (d.x2 ?? 0) * w, y2 = (d.y2 ?? 0) * h;
      return { x1: Math.min(x1, x2), y1: Math.min(y1, y2), x2: Math.max(x1, x2), y2: Math.max(y1, y2), markup: m, arrow: true, ax1: x1, ay1: y1, ax2: x2, ay2: y2 };
    }
    return null;
  }, [markups, selectedMarkupId, pageSize]);

  const getHandleAtPoint = useCallback((point) => {
    const b = getSelectedBbox();
    if (!b) return null;
    const hs = HANDLE_SIZE / 2;
    if (b.arrow) {
      if (Math.abs(point.x - b.ax1) <= hs && Math.abs(point.y - b.ay1) <= hs) return { handle: 'start', markup: b.markup };
      if (Math.abs(point.x - b.ax2) <= hs && Math.abs(point.y - b.ay2) <= hs) return { handle: 'end', markup: b.markup };
      return null;
    }
    for (const c of [{ id: 'nw', x: b.x1, y: b.y1 }, { id: 'ne', x: b.x2, y: b.y1 }, { id: 'se', x: b.x2, y: b.y2 }, { id: 'sw', x: b.x1, y: b.y2 }]) {
      if (Math.abs(point.x - c.x) <= hs && Math.abs(point.y - c.y) <= hs) return { handle: c.id, markup: b.markup };
    }
    return null;
  }, [getSelectedBbox]);

  const handlePanStart = useCallback((e) => {
    const el = containerRef.current;
    if (!el) return;
    e.preventDefault(); e.stopPropagation();
    setPanning(true);
    panStartRef.current = { clientX: e.clientX, clientY: e.clientY, scrollLeft: el.scrollLeft, scrollTop: el.scrollTop };
  }, []);

  useEffect(() => {
    if (!panning) return;
    const onMove = (e) => {
      if (!panStartRef.current || !containerRef.current) return;
      containerRef.current.scrollLeft = panStartRef.current.scrollLeft + (panStartRef.current.clientX - e.clientX);
      containerRef.current.scrollTop = panStartRef.current.scrollTop + (panStartRef.current.clientY - e.clientY);
    };
    const onUp = () => { setPanning(false); panStartRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [panning]);

  const translateMarkupData = useCallback((data, type, dxN, dyN) => {
    const out = { ...data };
    if (type === 'rectangle' || type === 'ellipse') { out.x = (out.x ?? 0) + dxN; out.y = (out.y ?? 0) + dyN; }
    else if (type === 'arrow') { out.x1 = (out.x1 ?? 0) + dxN; out.y1 = (out.y1 ?? 0) + dyN; out.x2 = (out.x2 ?? 0) + dxN; out.y2 = (out.y2 ?? 0) + dyN; }
    else if (type === 'pen' && Array.isArray(out.points)) out.points = out.points.map(([x, y]) => [x + dxN, y + dyN]);
    else if (type === 'text') { out.x = (out.x ?? 0) + dxN; out.y = (out.y ?? 0) + dyN; }
    return out;
  }, []);

  const handleContainerMouseDown = useCallback((e) => {
    if (tool !== TOOLS.hand) return;
    handlePanStart(e);
  }, [tool, handlePanStart]);

  const handleMouseDown = (e) => {
    setContextMenu(null);
    if (tool === TOOLS.hand) { handlePanStart(e); return; }
    const p = getCanvasPoint(e);
    if (!p || !pageSize.width) return;
    if (tool === TOOLS.select) {
      const handleHit = getHandleAtPoint(p);
      if (handleHit && !String(handleHit.markup.id).startsWith('opt-')) {
        setResizingMarkup(handleHit);
        resizeStartRef.current = { startX: p.x, startY: p.y, data: JSON.parse(JSON.stringify(handleHit.markup.data || {})) };
        return;
      }
      const hit = hitTestMarkup(p);
      if (hit && (hit.id === selectedMarkupId || String(hit.id) === String(selectedMarkupId)) && !String(hit.id).startsWith('opt-')) {
        setMovingMarkup(hit);
        moveStartRef.current = { startX: p.x, startY: p.y, data: JSON.parse(JSON.stringify(hit.data || {})) };
        return;
      }
      if (hit) {
        setSelectedMarkupId(hit.id);
        const canvas = canvasRef.current;
        if (canvas) {
          const rect = canvas.getBoundingClientRect();
          setContextMenu({ x: e.clientX - rect.left, y: e.clientY - rect.top, markupId: hit.id });
        }
      } else {
        setSelectedMarkupId(null);
      }
      return;
    }
    if (tool === TOOLS.pen) { setDrawing(true); setCurrentPath([p]); }
    else if ([TOOLS.rectangle, TOOLS.ellipse, TOOLS.arrow].includes(tool)) { setDrawing(true); setCurrentShape({ type: tool, x1: p.x, y1: p.y, x2: p.x, y2: p.y }); }
    else if (tool === TOOLS.text) { setTextInput({ x: p.x, y: p.y }); setTextValue(''); }
  };

  const handleMouseMove = (e) => {
    const p = getCanvasPoint(e);
    if (!p) return;
    if (resizingMarkup && resizeStartRef.current && pageSize.width && pageSize.height) {
      const { markup, handle } = resizingMarkup;
      const orig = resizeStartRef.current.data;
      const w = pageSize.width, h = pageSize.height;
      const newData = { ...orig };
      if (markup.type === 'arrow') {
        if (handle === 'start') { newData.x1 = p.x / w; newData.y1 = p.y / h; newData.x2 = orig.x2; newData.y2 = orig.y2; }
        else { newData.x1 = orig.x1; newData.y1 = orig.y1; newData.x2 = p.x / w; newData.y2 = p.y / h; }
      } else {
        const ox = orig.x ?? 0, oy = orig.y ?? 0, ow = orig.width ?? 0, oh = orig.height ?? 0;
        let fixedX, fixedY;
        if (handle === 'nw') { fixedX = ox + ow; fixedY = oy + oh; }
        else if (handle === 'ne') { fixedX = ox; fixedY = oy + oh; }
        else if (handle === 'se') { fixedX = ox; fixedY = oy; }
        else { fixedX = ox + ow; fixedY = oy; }
        newData.x = Math.min(fixedX, p.x / w); newData.y = Math.min(fixedY, p.y / h);
        newData.width = Math.abs(p.x / w - fixedX); newData.height = Math.abs(p.y / h - fixedY);
      }
      setMarkups((prev) => prev.map((m) => (m.id === markup.id || String(m.id) === String(markup.id) ? { ...m, data: newData } : m)));
      return;
    }
    if (movingMarkup && moveStartRef.current && pageSize.width && pageSize.height) {
      const { startX, startY, data: origData } = moveStartRef.current;
      const newData = translateMarkupData(origData || {}, movingMarkup.type, (p.x - startX) / pageSize.width, (p.y - startY) / pageSize.height);
      setMarkups((prev) => prev.map((m) => (m.id === movingMarkup.id || String(m.id) === String(movingMarkup.id) ? { ...m, data: newData } : m)));
      return;
    }
    if (drawing && tool === TOOLS.pen && currentPath.length) setCurrentPath((prev) => [...prev, p]);
    else if (drawing && currentShape) setCurrentShape((prev) => ({ ...prev, x2: p.x, y2: p.y }));
  };

  const addOptimisticMarkup = useCallback((page_number, type, data) => {
    setMarkups((prev) => [...prev, { id: `opt-${Date.now()}`, page_number, type, data, created_by: 'You', created_at: new Date().toISOString() }]);
  }, []);

  const handleMouseUp = () => {
    if (resizingMarkup && !String(resizingMarkup.markup.id).startsWith('opt-')) {
      const m = markups.find((x) => x.id === resizingMarkup.markup.id || String(x.id) === String(resizingMarkup.markup.id));
      if (m) updateMarkup(m.id, m.data);
      setResizingMarkup(null); resizeStartRef.current = null; return;
    }
    if (movingMarkup && moveStartRef.current && !String(movingMarkup.id).startsWith('opt-')) {
      const m = markups.find((x) => x.id === movingMarkup.id || String(x.id) === String(movingMarkup.id));
      if (m) updateMarkup(m.id, m.data);
      setMovingMarkup(null); moveStartRef.current = null; return;
    }
    if (tool === TOOLS.pen && drawing && currentPath.length > 2) {
      const w = pageSize.width, h = pageSize.height;
      const points = currentPath.map(({ x, y }) => [x / w, y / h]);
      const data = { points, color, strokeWidth, opacity };
      addOptimisticMarkup(currentPage, 'pen', data);
      scheduleSave(currentPage, 'pen', data); setCurrentPath([]);
    } else if (drawing && currentShape && [TOOLS.rectangle, TOOLS.ellipse, TOOLS.arrow].includes(tool)) {
      const { x1, y1, x2, y2 } = currentShape;
      const w = pageSize.width, h = pageSize.height;
      const data = {
        color, strokeWidth, opacity,
        fillColor: (tool === TOOLS.rectangle || tool === TOOLS.ellipse) ? fillColor : undefined,
        x1: x1 / w, y1: y1 / h, x2: x2 / w, y2: y2 / h,
        ...(tool === TOOLS.rectangle || tool === TOOLS.ellipse ? { x: Math.min(x1, x2) / w, y: Math.min(y1, y2) / h, width: Math.abs(x2 - x1) / w, height: Math.abs(y2 - y1) / h } : {}),
      };
      addOptimisticMarkup(currentPage, tool, data);
      scheduleSave(currentPage, tool, data); setCurrentShape(null);
    }
    setDrawing(false);
  };

  const handleTextSubmit = (val) => {
    const text = (val ?? textValue).trim();
    if (!textInput || !text) { setTextInput(null); setTextValue(''); return; }
    const w = pageSize.width, h = pageSize.height;
    const data = { x: textInput.x / w, y: textInput.y / h, text, color, fontSize: 14, opacity };
    addOptimisticMarkup(currentPage, 'text', data);
    scheduleSave(currentPage, 'text', data);
    setTextInput(null); setTextValue('');
  };

  // Canvas redraw
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !pageSize.width) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const pageMarkups = markups.filter((m) => m.page_number === currentPage);
    for (const m of pageMarkups) {
      const d = m.data || {};
      const c = d.color || '#1e88e5';
      const sw = d.strokeWidth ?? 2;
      const alpha = (d.opacity ?? 100) / 100;
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = c; ctx.fillStyle = c;
      ctx.lineWidth = sw; ctx.lineCap = 'round'; ctx.lineJoin = 'round';

      const hasStroke = c && c !== 'transparent';
      if (m.type === 'pen' && Array.isArray(d.points) && d.points.length >= 2) {
        if (hasStroke) {
          ctx.beginPath();
          const px = d.points.map(([a, b]) => [a * w, b * h]);
          ctx.moveTo(px[0][0], px[0][1]);
          for (let i = 1; i < px.length; i++) ctx.lineTo(px[i][0], px[i][1]);
          ctx.stroke();
        }
      } else if (m.type === 'rectangle') {
        const x = (d.x ?? 0) * w, y = (d.y ?? 0) * h, width = (d.width ?? 0) * w, height = (d.height ?? 0) * h;
        const fill = d.fillColor ?? d.fill;
        if (hasFill(fill)) { ctx.fillStyle = fill; ctx.fillRect(x, y, width, height); }
        if (hasStroke) { ctx.strokeStyle = c; ctx.strokeRect(x, y, width, height); }
      } else if (m.type === 'ellipse') {
        const x = (d.x ?? 0) * w, y = (d.y ?? 0) * h, rw = (d.width ?? 0) * w / 2, rh = (d.height ?? 0) * h / 2;
        const fill = d.fillColor ?? d.fill;
        ctx.beginPath(); ctx.ellipse(x + rw, y + rh, Math.abs(rw), Math.abs(rh), 0, 0, 2 * Math.PI);
        if (hasFill(fill)) { ctx.fillStyle = fill; ctx.fill(); }
        if (hasStroke) { ctx.strokeStyle = c; ctx.stroke(); }
      } else if (m.type === 'arrow') {
        const x1 = (d.x1 ?? 0) * w, y1 = (d.y1 ?? 0) * h, x2 = (d.x2 ?? 0) * w, y2 = (d.y2 ?? 0) * h;
        if (hasStroke) {
          ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
          const angle = Math.atan2(y2 - y1, x2 - x1), arrowLen = 12;
          ctx.beginPath(); ctx.moveTo(x2, y2);
          ctx.lineTo(x2 - arrowLen * Math.cos(angle - 0.4), y2 - arrowLen * Math.sin(angle - 0.4));
          ctx.moveTo(x2, y2);
          ctx.lineTo(x2 - arrowLen * Math.cos(angle + 0.4), y2 - arrowLen * Math.sin(angle + 0.4));
          ctx.stroke();
        }
      } else if (m.type === 'text' && d.text) {
        ctx.font = `${d.fontSize ?? 14}px sans-serif`;
        ctx.fillStyle = d.color || '#1e88e5';
        ctx.fillText(d.text, (d.x ?? 0) * w, (d.y ?? 0) * h);
      }
      ctx.globalAlpha = 1;

      if (selectedMarkupId != null && (m.id === selectedMarkupId || String(m.id) === String(selectedMarkupId))) {
        ctx.setLineDash([6, 4]); ctx.strokeStyle = '#2563eb'; ctx.lineWidth = 2;
        const d2 = m.data || {};
        if (m.type === 'pen' && Array.isArray(d2.points) && d2.points.length) {
          const px = d2.points.map(([a, b]) => [a * w, b * h]);
          let minX = px[0][0], maxX = px[0][0], minY = px[0][1], maxY = px[0][1];
          px.forEach(([x, y]) => { minX = Math.min(minX, x); maxX = Math.max(maxX, x); minY = Math.min(minY, y); maxY = Math.max(maxY, y); });
          ctx.strokeRect(minX - 4, minY - 4, maxX - minX + 8, maxY - minY + 8);
        } else if (m.type === 'rectangle') {
          ctx.strokeRect((d2.x ?? 0) * w - 4, (d2.y ?? 0) * h - 4, (d2.width ?? 0) * w + 8, (d2.height ?? 0) * h + 8);
        } else if (m.type === 'ellipse') {
          ctx.beginPath(); ctx.ellipse((d2.x ?? 0) * w + (d2.width ?? 0) * w / 2, (d2.y ?? 0) * h + (d2.height ?? 0) * h / 2, Math.abs((d2.width ?? 0) * w / 2) + 4, Math.abs((d2.height ?? 0) * h / 2) + 4, 0, 0, 2 * Math.PI); ctx.stroke();
        } else if (m.type === 'arrow') {
          ctx.strokeRect(Math.min((d2.x1 ?? 0) * w, (d2.x2 ?? 0) * w) - 4, Math.min((d2.y1 ?? 0) * h, (d2.y2 ?? 0) * h) - 4, Math.abs((d2.x2 ?? 0) * w - (d2.x1 ?? 0) * w) + 8, Math.abs((d2.y2 ?? 0) * h - (d2.y1 ?? 0) * h) + 8);
        } else if (m.type === 'text') {
          ctx.strokeRect((d2.x ?? 0) * w - 4, (d2.y ?? 0) * h - 16, 160, 24);
        }
        ctx.setLineDash([]);
      }
    }

    if (selectedMarkupId != null && tool === TOOLS.select && !movingMarkup && !resizingMarkup) {
      const sel = pageMarkups.find((m) => m.id === selectedMarkupId || String(m.id) === String(selectedMarkupId));
      if (sel && (sel.type === 'rectangle' || sel.type === 'ellipse' || sel.type === 'arrow')) {
        const d = sel.data || {}, hs = HANDLE_SIZE / 2;
        ctx.fillStyle = '#2563eb'; ctx.strokeStyle = '#fff'; ctx.lineWidth = 1;
        if (sel.type === 'arrow') {
          [[((d.x1 ?? 0) * w), ((d.y1 ?? 0) * h)], [((d.x2 ?? 0) * w), ((d.y2 ?? 0) * h)]].forEach(([hx, hy]) => { ctx.fillRect(hx - hs, hy - hs, HANDLE_SIZE, HANDLE_SIZE); ctx.strokeRect(hx - hs, hy - hs, HANDLE_SIZE, HANDLE_SIZE); });
        } else {
          const x = (d.x ?? 0) * w, y = (d.y ?? 0) * h, width = (d.width ?? 0) * w, height = (d.height ?? 0) * h;
          const x1 = Math.min(x, x + width), y1 = Math.min(y, y + height), x2 = Math.max(x, x + width), y2 = Math.max(y, y + height);
          [[x1, y1], [x2, y1], [x2, y2], [x1, y2]].forEach(([hx, hy]) => { ctx.fillRect(hx - hs, hy - hs, HANDLE_SIZE, HANDLE_SIZE); ctx.strokeRect(hx - hs, hy - hs, HANDLE_SIZE, HANDLE_SIZE); });
        }
      }
    }

    ctx.globalAlpha = opacity / 100;
    if (currentPath.length >= 2) {
      ctx.strokeStyle = color; ctx.lineWidth = strokeWidth; ctx.beginPath();
      ctx.moveTo(currentPath[0].x, currentPath[0].y);
      for (let i = 1; i < currentPath.length; i++) ctx.lineTo(currentPath[i].x, currentPath[i].y);
      ctx.stroke();
    }
    if (currentShape) {
      ctx.strokeStyle = color; ctx.lineWidth = strokeWidth;
      const { x1, y1, x2, y2 } = currentShape;
      if (tool === TOOLS.rectangle) {
        const rx = Math.min(x1, x2), ry = Math.min(y1, y2), rw = Math.abs(x2 - x1), rh = Math.abs(y2 - y1);
        if (hasFill(fillColor)) { ctx.fillStyle = fillColor; ctx.fillRect(rx, ry, rw, rh); }
        ctx.strokeRect(rx, ry, rw, rh);
      } else if (tool === TOOLS.ellipse) {
        const cx2 = (x1 + x2) / 2, cy2 = (y1 + y2) / 2;
        ctx.beginPath(); ctx.ellipse(cx2, cy2, Math.abs(x2 - x1) / 2, Math.abs(y2 - y1) / 2, 0, 0, 2 * Math.PI);
        if (hasFill(fillColor)) { ctx.fillStyle = fillColor; ctx.fill(); }
        ctx.stroke();
      } else if (tool === TOOLS.arrow) {
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
        const angle = Math.atan2(y2 - y1, x2 - x1), arrowLen = 12;
        ctx.beginPath(); ctx.moveTo(x2, y2);
        ctx.lineTo(x2 - arrowLen * Math.cos(angle - 0.4), y2 - arrowLen * Math.sin(angle - 0.4));
        ctx.moveTo(x2, y2);
        ctx.lineTo(x2 - arrowLen * Math.cos(angle + 0.4), y2 - arrowLen * Math.sin(angle + 0.4));
        ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;
  }, [markups, currentPage, pageSize, currentPath, currentShape, tool, color, strokeWidth, selectedMarkupId, fillColor, movingMarkup, resizingMarkup, opacity]);

  const portalBg = '#f5f7fa';
  const portalHeaderBg = '#ffffff';
  const portalBorder = '#e5e7eb';

  const sep = <span style={{ width: toolbarVertical ? '100%' : 1, height: toolbarVertical ? 1 : 20, background: 'rgba(255,255,255,0.2)', margin: '2px 0', flexShrink: 0 }} />;

  const toolBtn = (t, icon, title, extra) => (
    <button type="button" onClick={() => { setTool(t); setContextMenu(null); }} title={title} style={{ padding: 6, background: tool === t ? '#2563eb' : 'transparent', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', ...extra }}>{icon}</button>
  );

  const floatingToolbarStyle = {
    position: 'absolute',
    ...(toolbarDragged ? { left: toolbarPosition.x, top: toolbarPosition.y } : { right: 12, top: 12, left: 'auto' }),
    zIndex: 10,
    display: 'flex',
    flexDirection: toolbarVertical ? 'column' : 'row',
    alignItems: 'center',
    gap: 4,
    padding: '6px 8px',
    backgroundColor: 'rgba(55,65,81,0.95)',
    borderRadius: 10,
    boxShadow: '0 4px 20px rgba(0,0,0,0.2), 0 0 1px rgba(0,0,0,0.1)',
    border: '1px solid rgba(255,255,255,0.1)',
    flexWrap: 'nowrap',
    maxWidth: toolbarVertical ? 48 : undefined,
    maxHeight: toolbarVertical ? undefined : 48,
    overflow: 'visible',
  };

  const selectedMarkup = selectedMarkupId ? markups.find((x) => x.id === selectedMarkupId || String(x.id) === String(selectedMarkupId)) : null;
  const selData = selectedMarkup?.data || {};
  const mergeAndUpdate = (patch) => {
    if (selectedMarkup && !String(selectedMarkup.id).startsWith('opt-')) updateMarkup(selectedMarkup.id, { ...selData, ...patch });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: portalBg }}>
      <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px', backgroundColor: portalHeaderBg, borderBottom: `1px solid ${portalBorder}`, color: '#374151', gap: '8px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
          <button type="button" onClick={onClose} style={{ padding: '5px 10px', background: '#f3f4f6', color: '#374151', border: `1px solid ${portalBorder}`, borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>← Back</button>
          <span style={{ fontSize: '13px', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sheetTitle || 'Sheet'}</span>
          {saveStatus && (
            <span style={{ fontSize: '11px', color: saveStatus === 'saved' ? '#059669' : saveStatus === 'error' ? '#dc2626' : '#d97706' }} title={saveError || ''}>
              {saveStatus === 'saving' ? 'Saving…' : saveStatus === 'saved' ? 'Saved' : (saveError && saveError.length > 30 ? saveError.slice(0, 30) + '…' : saveError) || 'Save failed'}
            </span>
          )}
        </div>
        <span style={{ display: 'flex', alignItems: 'center', fontSize: '12px', color: '#6b7280' }}>
          <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={false} />
        </span>
      </div>

      <div ref={toolbarContainerRef} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
        {/* Floating toolbar */}
        <div ref={toolbarRef} style={floatingToolbarStyle}>
          <div onMouseDown={handleToolbarDragStart} style={{ cursor: 'move', padding: '4px', color: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }} title="Drag to move">⋮⋮</div>
          <button type="button" onClick={() => setToolbarVertical((v) => !v)} title={toolbarVertical ? 'Horizontal' : 'Vertical'} style={{ padding: 4, background: 'transparent', border: 'none', borderRadius: 4, cursor: 'pointer', color: 'rgba(255,255,255,0.9)' }}>{toolbarVertical ? '⇄' : '⇅'}</button>
          {sep}
          {toolBtn(TOOLS.hand, ToolIcons.hand, 'Pan (move PDF)')}
          {toolBtn(TOOLS.select, ToolIcons.select, 'Select')}
          {toolBtn(TOOLS.pen, ToolIcons.pen, 'Pen')}
          {toolBtn(TOOLS.rectangle, ToolIcons.rectangle, 'Rectangle')}
          {toolBtn(TOOLS.ellipse, ToolIcons.ellipse, 'Ellipse')}
          {toolBtn(TOOLS.arrow, ToolIcons.arrow, 'Arrow')}
          {toolBtn(TOOLS.text, ToolIcons.text, 'Text')}
          <button type="button" disabled title="Cloud (coming later)" style={{ padding: 6, background: 'transparent', color: 'rgba(255,255,255,0.4)', border: 'none', borderRadius: 6, cursor: 'not-allowed' }}>{ToolIcons.cloud}</button>
          <button type="button" disabled title="Measure (coming later)" style={{ padding: 6, background: 'transparent', color: 'rgba(255,255,255,0.4)', border: 'none', borderRadius: 6, cursor: 'not-allowed' }}>{ToolIcons.ruler}</button>
          {sep}
          {/* Fill & Border colour pickers */}
          <div style={{ position: 'relative', display: 'flex', flexDirection: toolbarVertical ? 'column' : 'row', gap: 4, flexShrink: 0 }}>
            <button type="button" onClick={() => { setShowBorderPicker(false); setShowFillPicker((v) => !v); }} title="Fill colour" style={{ padding: 4, background: hasFill(fillColor) ? fillColor : 'repeating-conic-gradient(#555 0% 25%, transparent 0% 50%) 50% / 12px 12px', border: '1px solid rgba(255,255,255,0.5)', borderRadius: 4, cursor: 'pointer', width: 24, height: 24, minWidth: 24, minHeight: 24 }} />
            {showFillPicker && (
              <div style={{ position: 'absolute', ...(toolbarVertical ? { top: '100%', left: 0, marginTop: 4 } : { right: '100%', top: 0, marginRight: 4 }), zIndex: 20, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, padding: 6, backgroundColor: 'rgba(30,41,59,0.98)', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
                {FILL_OPTIONS.map((opt) => (
                  <button key={opt.value === '' ? 'none' : opt.value} type="button" onClick={() => { setFillColor(opt.value); setShowFillPicker(false); if (selectedMarkup && !String(selectedMarkup.id).startsWith('opt-') && (selectedMarkup.type === 'rectangle' || selectedMarkup.type === 'ellipse')) updateMarkup(selectedMarkup.id, { ...(selectedMarkup.data || {}), fillColor: opt.value }); }} style={{ width: 20, height: 20, borderRadius: 4, backgroundColor: opt.value || '#374151', border: '1px solid rgba(255,255,255,0.3)', cursor: 'pointer', backgroundImage: opt.value === '' ? 'linear-gradient(45deg, #f44 50%, transparent 50%)' : undefined }} title={opt.label} />
                ))}
              </div>
            )}
            <button type="button" onClick={() => { setShowFillPicker(false); setShowBorderPicker((v) => !v); }} title="Border colour" style={{ padding: 4, background: color === 'transparent' ? 'repeating-conic-gradient(#555 0% 25%, transparent 0% 50%) 50% / 12px 12px' : color, border: '1px solid rgba(255,255,255,0.5)', borderRadius: 4, cursor: 'pointer', width: 24, height: 24, minWidth: 24, minHeight: 24 }} />
            {showBorderPicker && (
              <div style={{ position: 'absolute', ...(toolbarVertical ? { top: '100%', left: 0, marginTop: 4 } : { right: '100%', top: 0, marginRight: 4 }), zIndex: 20, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, padding: 6, backgroundColor: 'rgba(30,41,59,0.98)', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
                {BORDER_OPTIONS.map((opt) => (
                  <button key={opt.value} type="button" onClick={() => { setColor(opt.value); setShowBorderPicker(false); if (selectedMarkup && !String(selectedMarkup.id).startsWith('opt-')) updateMarkup(selectedMarkup.id, { ...(selectedMarkup.data || {}), color: opt.value }); }} style={{ width: 20, height: 20, borderRadius: 4, backgroundColor: opt.value === 'transparent' ? '#374151' : opt.value, border: '1px solid rgba(255,255,255,0.3)', cursor: 'pointer', backgroundImage: opt.value === 'transparent' ? 'linear-gradient(45deg, #f44 50%, transparent 50%)' : undefined }} title={opt.label} />
                ))}
              </div>
            )}
          </div>
          <select value={strokeWidth} onChange={(e) => setStrokeWidth(Number(e.target.value))} title="Stroke" style={{ padding: '2px 4px', borderRadius: 4, background: 'rgba(0,0,0,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', fontSize: '10px', maxWidth: toolbarVertical ? '100%' : 44 }}>
            {STROKE_WIDTHS.map((w) => <option key={w} value={w}>{w}px</option>)}
          </select>
          {/* Opacity */}
          <select value={opacity} onChange={(e) => { const v = Number(e.target.value); setOpacity(v); if (selectedMarkup && !String(selectedMarkup.id).startsWith('opt-')) updateMarkup(selectedMarkup.id, { ...(selectedMarkup.data || {}), opacity: v }); }} title="Opacity" style={{ padding: '2px 4px', borderRadius: 4, background: 'rgba(0,0,0,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', fontSize: '10px', maxWidth: toolbarVertical ? '100%' : 44 }}>
            {OPACITY_OPTIONS.map((o) => <option key={o} value={o}>{o}%</option>)}
          </select>
          {sep}
          {/* Zoom controls */}
          <select value={zoom} onChange={(e) => { const v = Number(e.target.value); pushUndo({ type: 'zoom', oldValue: zoom }); setZoom(v); }} title="Zoom" style={{ padding: '2px 4px', borderRadius: 4, background: 'rgba(0,0,0,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', fontSize: '10px', maxWidth: toolbarVertical ? '100%' : 52 }}>
            {ZOOM_OPTIONS.map((z) => <option key={z} value={z}>{Math.round(z * 100)}%</option>)}
          </select>
          <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <button type="button" onClick={() => { pushUndo({ type: 'zoom', oldValue: zoom }); setZoom((z) => Math.max(0.5, z - 0.25)); }} style={{ padding: '2px 6px', background: 'transparent', border: 'none', borderRadius: 4, cursor: 'pointer', color: 'white', fontSize: '14px' }} title="Zoom out">−</button>
            <button type="button" onClick={() => { pushUndo({ type: 'zoom', oldValue: zoom }); setZoom((z) => Math.min(3, z + 0.25)); }} style={{ padding: '2px 6px', background: 'transparent', border: 'none', borderRadius: 4, cursor: 'pointer', color: 'white', fontSize: '14px' }} title="Zoom in">+</button>
          </div>
          <button type="button" onClick={fitToView} title="Fit to view" style={{ padding: 6, background: 'transparent', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>{ToolIcons.fitView}</button>
          <button type="button" onClick={() => { const old = rotation; setRotation((r) => (r + 90) % 360); pushUndo({ type: 'rotation', oldValue: old }); }} title={`Rotate (${rotation}°)`} style={{ padding: 6, background: 'transparent', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>{ToolIcons.rotate}</button>
          {sep}
          {numPages > 1 && (
            <>
              <button type="button" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage <= 1} style={{ padding: 4, background: 'transparent', border: 'none', borderRadius: 4, cursor: currentPage <= 1 ? 'not-allowed' : 'pointer', color: 'white', opacity: currentPage <= 1 ? 0.5 : 1 }} title="Previous page">←</button>
              <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.9)' }}>{currentPage}/{numPages}</span>
              <button type="button" onClick={() => setCurrentPage((p) => Math.min(numPages, p + 1))} disabled={currentPage >= numPages} style={{ padding: 4, background: 'transparent', border: 'none', borderRadius: 4, cursor: currentPage >= numPages ? 'not-allowed' : 'pointer', color: 'white', opacity: currentPage >= numPages ? 0.5 : 1 }} title="Next page">→</button>
              {sep}
            </>
          )}
          <button type="button" onClick={() => selectedMarkupId && deleteMarkup(selectedMarkupId)} disabled={!selectedMarkupId} title="Delete selected" style={{ padding: 6, background: 'transparent', color: selectedMarkupId ? '#f87171' : 'rgba(255,255,255,0.4)', border: 'none', borderRadius: 6, cursor: selectedMarkupId ? 'pointer' : 'not-allowed' }}>{ToolIcons.delete}</button>
          <button type="button" onClick={handleUndo} disabled={undoStack.length === 0} title="Undo" style={{ padding: 6, background: 'transparent', color: undoStack.length ? 'white' : 'rgba(255,255,255,0.4)', border: 'none', borderRadius: 6, cursor: undoStack.length ? 'pointer' : 'not-allowed' }}>{ToolIcons.undo}</button>
          <button type="button" onClick={handleRedo} disabled={redoStack.length === 0} title="Redo" style={{ padding: 6, background: 'transparent', color: redoStack.length ? 'white' : 'rgba(255,255,255,0.4)', border: 'none', borderRadius: 6, cursor: redoStack.length ? 'pointer' : 'not-allowed' }}>{ToolIcons.redo}</button>
          {sep}
          <button type="button" onClick={openAudit} title="Audit log" style={{ padding: 6, background: 'transparent', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '10px' }}>Audit</button>
          <button type="button" onClick={() => setSettingsOpen(true)} title="Settings" style={{ padding: 6, background: 'transparent', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>{ToolIcons.settings}</button>
          <button type="button" onClick={() => setCompareOpen(true)} title="Compare revisions" style={{ padding: 6, background: 'transparent', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>{ToolIcons.compare}</button>
          <button type="button" onClick={() => setHelpOpen(true)} title="Help" style={{ padding: 6, background: 'transparent', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>{ToolIcons.help}</button>
          <button type="button" onClick={onClose} title="Close / Back" style={{ padding: 6, background: 'transparent', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>{ToolIcons.fullscreen}</button>

          {/* Selected markup inline controls */}
          {selectedMarkup && tool === TOOLS.select && (
            <>
              {sep}
              <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.7)', textAlign: 'center' }}>Sel.</span>
              <select value={selData.color ?? color} onChange={(e) => mergeAndUpdate({ color: e.target.value })} title="Border colour" style={{ padding: '2px', borderRadius: 4, background: 'rgba(0,0,0,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', fontSize: '9px', maxWidth: toolbarVertical ? '100%' : 55 }}>
                {BORDER_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
              {(selectedMarkup.type === 'rectangle' || selectedMarkup.type === 'ellipse') && (
                <select value={selData.fillColor ?? ''} onChange={(e) => mergeAndUpdate({ fillColor: e.target.value })} title="Fill" style={{ padding: '2px', borderRadius: 4, background: 'rgba(0,0,0,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', fontSize: '9px', maxWidth: toolbarVertical ? '100%' : 55 }}>
                  {FILL_OPTIONS.map((opt) => <option key={opt.value === '' ? 'none' : opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              )}
              <select value={selData.strokeWidth ?? strokeWidth} onChange={(e) => mergeAndUpdate({ strokeWidth: Number(e.target.value) })} title="Stroke" style={{ padding: '2px', borderRadius: 4, background: 'rgba(0,0,0,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', fontSize: '9px', maxWidth: toolbarVertical ? '100%' : 40 }}>
                {STROKE_WIDTHS.map((w) => <option key={w} value={w}>{w}px</option>)}
              </select>
              <select value={selData.opacity ?? 100} onChange={(e) => mergeAndUpdate({ opacity: Number(e.target.value) })} title="Opacity" style={{ padding: '2px', borderRadius: 4, background: 'rgba(0,0,0,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', fontSize: '9px', maxWidth: toolbarVertical ? '100%' : 40 }}>
                {OPACITY_OPTIONS.map((o) => <option key={o} value={o}>{o}%</option>)}
              </select>
            </>
          )}
        </div>

        {/* Context menu (Edit / Duplicate / Reference / Delete) */}
        {contextMenu && (
          <div style={{ position: 'absolute', left: contextMenu.x + 60, top: contextMenu.y + 60, zIndex: 60, display: 'flex', gap: 0, backgroundColor: 'rgba(30,41,59,0.97)', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.3)', padding: '4px', border: '1px solid rgba(255,255,255,0.1)' }}>
            {[
              { icon: ToolIcons.edit, label: 'Edit', onClick: () => { setContextMenu(null); } },
              { icon: ToolIcons.duplicate, label: 'Duplicate', onClick: () => { duplicateMarkup(contextMenu.markupId); } },
              { icon: ToolIcons.reference, label: 'Reference', onClick: () => { setContextMenu(null); alert('Reference linking coming soon'); } },
              { icon: ToolIcons.delete, label: 'Delete', onClick: () => { deleteMarkup(contextMenu.markupId); } },
            ].map((item) => (
              <button key={item.label} type="button" onClick={item.onClick} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '8px 14px', background: 'transparent', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '10px' }}>
                {item.icon}
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* PDF content area */}
        <div ref={containerRef} onMouseDown={handleContainerMouseDown} style={{ flex: 1, overflow: 'auto', padding: '16px', paddingTop: 16, backgroundColor: portalBg, cursor: tool === TOOLS.hand ? (panning ? 'grabbing' : 'grab') : undefined }}>
          {pdfUrl && (
            <div style={{ position: 'relative', display: 'block', width: pageSize.width, height: pageSize.height, minHeight: 200, margin: '0 auto', transform: rotation ? `rotate(${rotation}deg)` : undefined, transformOrigin: 'center center' }}>
              {!pageReady && pageSize.height > 0 && (
                <div style={{ position: 'absolute', inset: 0, backgroundColor: portalBg, zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontSize: 14 }}>Loading page…</div>
              )}
              <Document file={pdfUrl} onLoadSuccess={onDocumentLoadSuccess} loading={<div style={{ color: '#6b7280', padding: 24, backgroundColor: portalBg }}>Loading PDF…</div>}>
                <Page pageNumber={currentPage} onLoadSuccess={onPageLoadSuccess} width={effectiveWidth * zoom} renderTextLayer={false} renderAnnotationLayer={false} />
              </Document>
              {pageSize.height > 0 && (
                <canvas
                  ref={canvasRef}
                  width={pageSize.width}
                  height={pageSize.height}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  style={{
                    position: 'absolute', left: 0, top: 0,
                    width: pageSize.width, height: pageSize.height,
                    cursor: tool === TOOLS.hand ? (panning ? 'grabbing' : 'grab') : tool === TOOLS.select ? 'pointer' : 'crosshair',
                    pointerEvents: 'auto',
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Text input popover */}
      {textInput && (
        <div style={{ position: 'fixed', left: Math.min(textInput.x + 20, window.innerWidth - 280), top: Math.min(textInput.y, window.innerHeight - 60), zIndex: 10000, background: 'white', padding: '8px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input type="text" placeholder="Enter text…" value={textValue} onChange={(e) => setTextValue(e.target.value)} autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') handleTextSubmit(textValue); if (e.key === 'Escape') { setTextInput(null); setTextValue(''); } }}
            style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', minWidth: 180, fontSize: '14px' }} />
          <button type="button" onClick={() => handleTextSubmit(textValue)} style={{ padding: '6px 12px', background: '#1e88e5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Add</button>
          <button type="button" onClick={() => { setTextInput(null); setTextValue(''); }} style={{ padding: '6px 12px', background: '#6b7280', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
        </div>
      )}

      {/* Audit log modal */}
      {auditOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 10002, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={() => setAuditOpen(false)}>
          <div style={{ background: '#fff', color: '#374151', borderRadius: '12px', maxWidth: 520, width: '100%', maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', border: `1px solid ${portalBorder}` }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${portalBorder}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>Audit log</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={fetchAudit} disabled={auditLoading} style={{ padding: '6px 12px', background: '#f3f4f6', color: '#374151', border: `1px solid ${portalBorder}`, borderRadius: '6px', cursor: auditLoading ? 'wait' : 'pointer', fontSize: '13px' }}>Refresh</button>
                <button type="button" onClick={() => setAuditOpen(false)} style={{ padding: '6px 12px', background: '#374151', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Close</button>
              </div>
            </div>
            <div style={{ overflow: 'auto', padding: '12px', flex: 1 }}>
              {auditError && <p style={{ color: '#dc2626', margin: '0 0 12px 0', fontSize: '13px' }}>{auditError}</p>}
              {auditLoading ? <p style={{ color: '#6b7280', margin: 0 }}>Loading…</p> :
               !auditError && auditLog.length === 0 ? <p style={{ color: '#6b7280', margin: 0 }}>No audit entries yet.</p> :
               !auditError ? (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {auditLog.map((entry) => {
                    const time = entry.created_at ? new Date(entry.created_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' }) : '—';
                    const actionLabel = ((entry.action || '').replace(/_/g, ' '));
                    let detailsObj = entry.details;
                    if (typeof detailsObj === 'string') { try { detailsObj = JSON.parse(detailsObj); } catch (_) { detailsObj = null; } }
                    const detailsStr = detailsObj && typeof detailsObj === 'object' ? [detailsObj.type, detailsObj.page_number != null ? `page ${detailsObj.page_number}` : ''].filter(Boolean).join(', ') || null : null;
                    return (
                      <li key={entry.id} style={{ padding: '12px', borderBottom: `1px solid ${portalBorder}`, fontSize: '13px' }}>
                        <div style={{ color: '#6b7280', marginBottom: 4 }}>{time}</div>
                        <div><strong>{entry.user_label || 'Unknown'}</strong> — {actionLabel.charAt(0).toUpperCase() + actionLabel.slice(1)}{detailsStr ? ` (${detailsStr})` : ''}</div>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Settings modal */}
      {settingsOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 10002, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', padding: 24 }} onClick={() => setSettingsOpen(false)}>
          <div style={{ background: '#fff', color: '#374151', borderRadius: '12px', width: 340, maxHeight: '80vh', overflow: 'auto', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', border: `1px solid ${portalBorder}`, marginTop: 48 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${portalBorder}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Settings</h3>
              <button type="button" onClick={() => setSettingsOpen(false)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            <div style={{ display: 'flex', borderBottom: `1px solid ${portalBorder}` }}>
              {[{ id: 'config', label: 'Configuration' }, { id: 'nav', label: 'Navigation' }, { id: 'appear', label: 'Appearance' }].map((tab) => (
                <button key={tab.id} type="button" onClick={() => setSettingsTab(tab.id)} style={{ flex: 1, padding: '10px 8px', background: settingsTab === tab.id ? '#fff' : '#f9fafb', color: settingsTab === tab.id ? '#2563eb' : '#6b7280', border: 'none', borderBottom: settingsTab === tab.id ? '2px solid #2563eb' : '2px solid transparent', cursor: 'pointer', fontSize: '12px', fontWeight: settingsTab === tab.id ? 600 : 400 }}>{tab.label}</button>
              ))}
            </div>
            <div style={{ padding: '16px 20px' }}>
              {settingsTab === 'config' && (
                <>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600 }}>Performance Optimization</h4>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <strong style={{ fontSize: '13px' }}>Progressive display</strong>
                      <div style={{ width: 36, height: 20, borderRadius: 10, backgroundColor: '#d1d5db', position: 'relative', cursor: 'pointer' }}><div style={{ width: 16, height: 16, borderRadius: 8, backgroundColor: '#fff', position: 'absolute', top: 2, left: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} /></div>
                    </div>
                    <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>Shows incremental updates of the view and allows for more responsive interaction with the model.</p>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <strong style={{ fontSize: '13px' }}>Vector Viewing</strong>
                      <div style={{ width: 36, height: 20, borderRadius: 10, backgroundColor: '#2563eb', position: 'relative', cursor: 'pointer' }}><div style={{ width: 16, height: 16, borderRadius: 8, backgroundColor: '#fff', position: 'absolute', top: 2, right: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} /></div>
                    </div>
                    <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>Provides increased zooming, snapping and access to PDF vector data. If turned off, all PDFs will be rasterized.</p>
                  </div>
                  <h4 style={{ margin: '16px 0 12px 0', fontSize: '14px', fontWeight: 600 }}>Display</h4>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: '13px' }}>Display Units</span>
                    <select style={{ padding: '4px 8px', borderRadius: 4, border: `1px solid ${portalBorder}`, fontSize: '12px' }}><option>File units</option><option>Metric</option><option>Imperial</option></select>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px' }}>Precision</span>
                    <select style={{ padding: '4px 8px', borderRadius: 4, border: `1px solid ${portalBorder}`, fontSize: '12px' }}><option>File precision</option><option>0</option><option>0.0</option><option>0.00</option></select>
                  </div>
                </>
              )}
              {settingsTab === 'nav' && (
                <>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600 }}>Zoom</h4>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <strong style={{ fontSize: '13px' }}>Reverse mouse zoom direction</strong>
                      <div style={{ width: 36, height: 20, borderRadius: 10, backgroundColor: '#2563eb', position: 'relative', cursor: 'pointer' }}><div style={{ width: 16, height: 16, borderRadius: 8, backgroundColor: '#fff', position: 'absolute', top: 2, right: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} /></div>
                    </div>
                    <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>Toggles direction of zooming in and out.</p>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <strong style={{ fontSize: '13px', display: 'block', marginBottom: 4 }}>Scroll Speed</strong>
                    <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 6px 0' }}>Changes sensitivity of the mouse scroll wheel when zooming</p>
                    <input type="range" min="1" max="10" defaultValue="4" style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <strong style={{ fontSize: '13px', display: 'block', marginBottom: 4 }}>Drag Speed</strong>
                    <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 6px 0' }}>Changes sensitivity of mouse movement with the zoom tool</p>
                    <input type="range" min="1" max="10" defaultValue="4" style={{ width: '100%' }} />
                  </div>
                  <h4 style={{ margin: '16px 0 12px 0', fontSize: '14px', fontWeight: 600 }}>Mouse</h4>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div><strong style={{ fontSize: '13px' }}>Open properties on select</strong><p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>Always show properties upon selecting object.</p></div>
                    <div style={{ width: 36, height: 20, borderRadius: 10, backgroundColor: '#d1d5db', position: 'relative', cursor: 'pointer' }}><div style={{ width: 16, height: 16, borderRadius: 8, backgroundColor: '#fff', position: 'absolute', top: 2, left: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} /></div>
                  </div>
                </>
              )}
              {settingsTab === 'appear' && (
                <>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600 }}>Existing behavior</h4>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <div><strong style={{ fontSize: '13px' }}>2D Sheet Color</strong><p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>Switch sheet color white to black.</p></div>
                      <div style={{ width: 36, height: 20, borderRadius: 10, backgroundColor: '#d1d5db', position: 'relative', cursor: 'pointer' }}><div style={{ width: 16, height: 16, borderRadius: 8, backgroundColor: '#fff', position: 'absolute', top: 2, left: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} /></div>
                    </div>
                  </div>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <div><strong style={{ fontSize: '13px' }}>Loading Animation</strong><p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>Animate lines during loading.</p></div>
                      <div style={{ width: 36, height: 20, borderRadius: 10, backgroundColor: '#d1d5db', position: 'relative', cursor: 'pointer' }}><div style={{ width: 16, height: 16, borderRadius: 8, backgroundColor: '#fff', position: 'absolute', top: 2, left: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} /></div>
                    </div>
                  </div>
                </>
              )}
              <div style={{ marginTop: 20, textAlign: 'center' }}>
                <button type="button" style={{ padding: '8px 20px', background: '#fff', color: '#2563eb', border: '2px solid #2563eb', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Restore all default settings</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help modal */}
      {helpOpen && (<HelpModal onClose={() => setHelpOpen(false)} portalBorder={portalBorder} />)}

      {/* Compare revisions modal */}
      {compareOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 10002, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={() => setCompareOpen(false)}>
          <div style={{ background: '#fff', color: '#374151', borderRadius: '12px', maxWidth: 600, width: '100%', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', border: `1px solid ${portalBorder}` }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${portalBorder}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Compare Revisions</h3>
              <button type="button" onClick={() => setCompareOpen(false)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: 16 }}>Compare two revisions of this sheet to see what changed. Modifications between revisions will be highlighted in red.</p>
              <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: 4 }}>Revision A (Older)</label>
                  <select style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${portalBorder}`, fontSize: '13px' }}>
                    <option>Select revision…</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: 4 }}>Revision B (Newer)</label>
                  <select style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${portalBorder}`, fontSize: '13px' }}>
                    <option>Current</option>
                  </select>
                </div>
              </div>
              <div style={{ background: '#f9fafb', borderRadius: 8, padding: 20, textAlign: 'center', color: '#6b7280', border: `1px dashed ${portalBorder}` }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 500 }}>Compared Documents</p>
                <p style={{ margin: 0, fontSize: '12px' }}>Select two revisions above to compare. Changes between revisions will be displayed as a red overlay on the sheet, showing additions, deletions, and modifications.</p>
              </div>
              <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setCompareOpen(false)} style={{ padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: `1px solid ${portalBorder}`, borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
                <button type="button" disabled style={{ padding: '8px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'not-allowed', fontSize: '13px', opacity: 0.5 }}>Compare</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PdfMarkupViewer;
